/**
 * Calendar-day helpers shared by every per-day chart in this folder.
 *
 * Extracted when the Search Progress tab landed and needed the same month-tick
 * axis `OutreachLines` already drew. A second copy of the formatter would have
 * been a second place for a year boundary to render differently on two charts
 * sitting one tab apart.
 *
 * Everything here is UTC. The report's day keys are composed in UTC by the
 * backend (`_day_key`), so parsing them through the local-time `Date` ctor would
 * shift every point by a day for anyone west of Greenwich.
 */

export const DAY_MS = 86_400_000;

const MONTH_NAMES = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

/** `YYYY-MM-DD` exactly — the shape `_day_key` emits and nothing else. */
const DAY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/**
 * True for a key this module can do arithmetic on.
 *
 * Round-trips rather than range-checking each field: `Date.UTC` happily ROLLS
 * OVER an out-of-range month or day (`2026-13-40` becomes 2027-02-09), so a
 * shape test plus a NaN test would accept a nonsense date and silently plot it a
 * year away. If the parsed instant does not format back to the string it came
 * from, the string was not a calendar day.
 */
export function isDayKey(day: string): boolean {
  if (!DAY_PATTERN.test(day)) return false;
  const ms = dayToMs(day);
  if (Number.isNaN(ms)) return false;
  return msToDay(ms) === day;
}

/**
 * `YYYY-MM-DD` → epoch ms at UTC midnight.
 *
 * Exported for `timeRange.ts`, which does the same UTC arithmetic to bucket days
 * into ISO weeks. A second local copy of this pair is how the two modules would
 * end up disagreeing about a day.
 */
export function dayToMs(day: string): number {
  const [year, month, date] = day.split("-").map(Number);
  return Date.UTC(year, month - 1, date);
}

/** The inverse of {@link dayToMs}. */
export function msToDay(ms: number): string {
  return new Date(ms).toISOString().slice(0, 10);
}

/**
 * Every calendar day from `first` to `last` inclusive, capped to the most recent
 * `maxDays`.
 *
 * The cap is applied from the END, mirroring the backend's `_day_range`: a row
 * with a typo'd year (Thrive validates nothing on a date) would otherwise
 * materialise tens of thousands of points before anything trimmed them, and the
 * point a reader wants is always the recent end. Callers are expected to fold
 * whatever falls off the front into their own baseline — see
 * `stageProgressFrom`.
 */
export function dayRange(first: string, last: string, maxDays: number): string[] {
  if (!isDayKey(first) || !isDayKey(last)) return [];
  let start = dayToMs(first);
  const end = dayToMs(last);
  if (end < start) return [first];
  let span = Math.round((end - start) / DAY_MS);
  if (span >= maxDays) {
    start = end - (maxDays - 1) * DAY_MS;
    span = maxDays - 1;
  }
  return Array.from({ length: span + 1 }, (_, offset) => msToDay(start + offset * DAY_MS));
}

/**
 * `2026-01-05` (or `2026-01`) → `Jan`, or `Jan 26` in January so a year boundary
 * is readable without a second axis row.
 */
export function monthLabel(day: string): string {
  const [year, month] = day.split("-");
  const index = Number(month) - 1;
  const name = MONTH_NAMES[index] ?? day;
  return index === 0 ? `${name} ${year.slice(2)}` : name;
}

/**
 * `2026-07-26` → `Jul 26, 2026` — the full day, for a tooltip heading.
 *
 * Formatted from the STRING's own parts rather than through
 * `toLocaleDateString`: the day keys are UTC (`_day_key` composes them that
 * way), and handing a UTC midnight to a local-time formatter renders the
 * previous day for every reader west of Greenwich. The month axis has always
 * had this problem hidden from it — a label that is one day off still lands in
 * the same month eleven times out of twelve — but a tooltip that names a single
 * day would show it every time.
 */
export function dayLabel(day: string): string {
  const [year, month, date] = day.split("-");
  const name = MONTH_NAMES[Number(month) - 1];
  if (!name) return day;
  return `${name} ${Number(date)}, ${year}`;
}

/** `2026-07-26` → `Jul 2026` — the month WITH its year, for a tooltip heading. */
export function monthYearLabel(day: string): string {
  const [year, month] = day.split("-");
  const name = MONTH_NAMES[Number(month) - 1];
  if (!name) return day;
  return `${name} ${year}`;
}

/** One entry per month boundary in `days`, carrying its index on the axis. */
export function monthTicks(days: readonly string[]): { day: string; index: number }[] {
  const ticks = days
    .map((day, index) => ({ day, index }))
    .filter((tick, i, all) => i === 0 || tick.day.slice(0, 7) !== all[i - 1].day.slice(0, 7));

  // Drop a tick that would be drawn on top of the next one.
  //
  // Index 0 is always a tick, so a range STARTING late in a month puts that
  // month's label a couple of days from the next month's. Observed on search
  // 4379, whose data begins 2026-01-30: "Jan 26" and "Feb" rendered two days
  // apart across a 203-day axis and overlapped into an unreadable smudge. The
  // January label was also carrying a single day of data, so dropping it loses
  // nothing a reader wanted.
  //
  // Expressed as a fraction of the span rather than in pixels: this module has
  // no idea how wide the chart is drawn, and every caller renders the same
  // ticks into a different width.
  const MIN_GAP = 0.04;
  const span = days.length - 1;
  if (span <= 0) return ticks;

  return ticks.filter((tick, i) => {
    const next = ticks[i + 1];
    if (!next) return true; // the last tick has nothing to collide with
    return (next.index - tick.index) / span >= MIN_GAP;
  });
}

/**
 * How many month ticks to skip between drawn axis labels.
 *
 * Shared by the two line charts that thin their axis — `StageLines` and
 * `OutreachLines`. (`AdditionsLine` draws month-year labels unthinned and does
 * not use this.) It lived twice, byte-identical, with the two
 * magic numbers repeated — so a change to the density heuristic had to be made
 * in two files or silently diverge (PR #752, Agent A).
 *
 * THE `+ 1` IS THE FIX FOR A REGRESSION, not a fudge. The original was
 * `ceil(ticks / 12)`; the width-derived version without the `+ 1` computes
 * `floor(464 / 40) = 11` slots at the 480-unit fallback, so a 12-month series
 * went from every month to every OTHER month — a labelling regression on the
 * DASHBOARD tab, which never had the scaling bug this work was about. One extra
 * slot restores parity there and still grows with the width.
 *
 * The floor of 4 keeps a very narrow chart from asking for a label per tick.
 */
export function labelStride(tickCount: number, plotWidth: number): number {
  const slots = Math.max(4, Math.floor(plotWidth / 40) + 1);
  return Math.ceil(tickCount / slots);
}
